<template>
  <div class="">
    <v-form @submit.prevent="submitEvent" class="mt-5">
      <!-- STEPPER HEADERS -->
      <v-stepper
          v-model="stepper"
          border="primary"
          alt-labels >
        <v-stepper-header
          class="elevation-0 bg-header-gradient"
          bold="true"
        >
          <v-stepper-item
              title="KPI Name & Aggregation Levels"
              :complete="stepper > 0"
              :error="stepper === 0 && messages.length !== 0"
              color="blue-darken-4"
              value="1"/>

          <v-divider />

          <v-stepper-item
              title="Dimensions & Weights"
              :complete="stepper > 1"
              :error="stepper === 1 && messages.length !== 0"
              color="blue-darken-4"
              value="2" />

          <v-divider />

          <v-stepper-item
              title="Sectors & Weights"
              :complete="stepper > 2"
              :error="stepper === 2 && messages.length !== 0"
              color="blue-darken-4"
              value="3" />

          <v-divider />

          <v-stepper-item
              title="Margins"
              :complete="stepper > 3"
              :error="stepper === 3 && messages.length !== 0"
              color="blue-darken-4"
              value="4" />
        </v-stepper-header>
        <v-divider />
        <div v-if="currentFormStep > 0"
            class="bg-blue-accent-2 pa-2">
          <p><b>{{ form.kpi.name }}</b> | {{ selectedFunctionalUnit }}</p>
        </div>
        <!-- MAIN CONTENT OF STEPPER -->
        <v-stepper-window class="px-10 mt-1">

          <!-- CONSECUTIVE STEPS -->
          <v-stepper-window-item
              :reverse-transition="false"
              :transition="false"
              value="1">
            <FirstStep
                v-model="form"
                :kpi-types="kpisTypes"
                @name-changed="onNameChange"
                class="mt-6" />
          </v-stepper-window-item>

          <v-window-item
              :reverse-transition="false"
              :transition="false"
              value="2">
            <SecondStep
                v-model="form"
                :accumulated-weights="accumulatedWeights"
                :ignored-weights-server="ignoredWeights"
                class="mt-2"
            ></SecondStep>
          </v-window-item>

          <v-window-item
              :reverse-transition="false"
              :transition="false"
              value="3">
            <ThirdStep
                v-model="form"
                :accumulated-weights="accumulatedWeights"
                :ignored-weights-server="ignoredWeights"
                class="mt-2"
            ></ThirdStep>
          </v-window-item>

          <v-window-item
              :reverse-transition="false"
              :transition="false"
              value="4">
            <FourthStep
                v-model="form"
                class="mt-2"
            ></FourthStep>
          </v-window-item>
        </v-stepper-window>
        <v-divider class="mb-4 mt-5" />
        <v-stepper-actions>
          <template v-slot:prev>
            <v-btn
                text="previous"
                rounded
                prepend-icon="fa fa-arrow-left"
                variant="outlined"
                color="warning"
                @click.prevent="back"
                @click="$event.target.blur()"
            />
          </template>

          <template v-slot:next>
            <v-btn
                text="next"
                rounded
                append-icon="fa fa-arrow-right"
                variant="outlined"
                color="green"
                @click.prevent="proceed"
                @click="$event.target.blur()"
            />
          </template>
        </v-stepper-actions>
      </v-stepper>

      <div v-if="messages.length > 0" class="mb-2">
        <v-alert
          v-for="(message, index) in messages"
          :key="`message${index}`"
          type="error"
          variant="tonal"
          class="my-2"
          dismissible
        >
          {{ message }}
        </v-alert>
      </div>
      <v-row no-gutters class="mt-5">
        <v-spacer />
        <v-col cols="auto">
          <v-btn
              :disabled="currentFormStep <= 1"
              @click="emit('save')"
              color="blue"
              class="mr-3"
              variant="tonal"
              rounded="xl"
          >
            Save and Exit
          </v-btn>
          <v-btn
            v-if="currentFormStep === 3"
            type="submit"
            text="SUBMIT"
            rounded
            color="green"
            variant="flat" />

        </v-col>
      </v-row>
    </v-form>
    <br />
  </div>
</template>

<script setup>
import FirstStep from "./form_steps/FirstStep.vue";
import SecondStep from "./form_steps/SecondStep.vue";
import ThirdStep from "./form_steps/ThirdStep.vue";
import FourthStep from "./form_steps/FourthStep.vue";
import "vue-step-progress/dist/main.css";
import { computed, ref, watch } from "vue";
import Utils from "../../Mixins/Utils";
const emit = defineEmits(["submit", "save"]);

const props = defineProps({
  projectId: {
    type: Number,
    required: true,
  },
  modelValue: {
    type: Object,
    required: true,
  },
  prototypes: {
    type: Object,
    required: false,
  },
  kpisTypes: {
    type: Array,
    required: true,
  },
  defaultKpiValues: {
    type: Object,
    required: false,
  },
  accumulatedWeights: {
    type: Object,
    required: false,
    default: null,
  },
  ignoredWeights: {
    type: Array,
    required: false,
    default: function () {
      return [];
    },
  },
});

let currentFormStep = ref(0);
let cannotProceed = ref(false);
let messages = ref([]);
let temp = ref(1);
let stepper = ref(0);

const formSteps = [
  "KPI selection, aggregation levels and values",
  "Dimension and weights",
  "Sectors and weights",
  "Margins",
];

const form = props.modelValue;

const sectorsIsolates = computed(() => {
  return form.kpi.sectors_attributes;
});
const selectedFunctionalUnit = computed(() => {
  const obj = props.kpisTypes.find((type) => type.text === form.kpi.name);
  if (obj !== undefined) return obj.functionalUnit;
  return null;
});
const marginsValidationHelper = computed(() => {
  return marginsValidationMethod();
});

watch(
  () => temp,
  () => {
    temp = 2;
  }
);

function errorStepper(step) {
  if (messages.value.length !== 0) {
    if (step !== stepper.value) {
      return false;
    }
    return true;
  }

  return true;
}

function submitEvent() {
  messages.value = [];
  proceedFourthStep();
  if (messages.value.length === 0) emit("submit");
}

function proceed() {
  if (currentFormStep.value === 0) proceedFirstStep();
  else if (currentFormStep.value === 1) proceedSecondStep();
  else if (currentFormStep.value === 2) proceedThirdStep();

  if (messages.value.length === 0) {
    currentFormStep.value += 1;
    stepper.value++;
  }
}

function back() {
  currentFormStep.value -= 1;
  stepper.value--;
}
// there might be a better way to do this
function proceedFirstStep() {
  messages.value = [];
  const isLikert = form.kpi.likert === "true";
  if (form.kpi.name === null || form.kpi.name === "")
    messages.value.push("Empty KPI name.");
  if (form.aggregationLevels.length === 0) {
    messages.value.push("No aggregation level selected.");
  }
  let aggregationLevelValues = true;
  let likertGreaterThanFive = false;
  form.aggregationLevels.forEach(function callbackFN(level) {
    if (level.value === "") aggregationLevelValues = false;
    if (level.value > 5 && isLikert) likertGreaterThanFive = true;
  });
  if (!aggregationLevelValues)
    messages.value.push("Missing values from aggregation levels.");
  if (likertGreaterThanFive)
    messages.value.push(
      "Likert is selected. Please provide values between 0-5."
    );
}

function proceedSecondStep() {
  messages.value = [];
  if (
    form.kpi.dimension_attributes.name === undefined ||
    form.kpi.dimension_attributes.name === null
  ) {
    messages.value.push("No dimension selected");
  }

  let aggregationLevelValues = true;
  let weightsAboveUnit = false;
  let zeroOrNegativeValues = false;
  form.aggregationLevels.forEach(function callbackFn(level) {
    if (level.dimension_weight === "" || level.dimension_weight === undefined)
      aggregationLevelValues = false;
    if (level.dimension_weight > 1) weightsAboveUnit = true;

    if(level.dimension_weight <= 0) zeroOrNegativeValues = true;
  });
  if (!aggregationLevelValues)
    messages.value.push("Missing weights from aggregation levels.");
  if (weightsAboveUnit) messages.value.push("Some weights are >=1.");
  if (zeroOrNegativeValues && aggregationLevelValues) messages.value.push("Some weights are <= 0.")
}

function proceedThirdStep() {
  messages.value = [];
  const form = props.modelValue;
  let weightsAboveUnit = false;
  let zeroOrNegativeValues = false;
  form.kpi.sectors_attributes.forEach(function callbackFn(sector) {
    form.aggregationLevels.forEach(function callbackFn(level, index) {
      const sectorKey = `sector_${sector.name}_weight`;

      if (level[sectorKey] === undefined || level[sectorKey] === "")
        if (!messages.value.includes("No weights assigned to all sectors."))
          messages.value.push("No weights assigned to all sectors.");
      if (level[sectorKey] > 1) weightsAboveUnit = true;
      if (level[sectorKey] && level[sectorKey] <= 0) zeroOrNegativeValues = true;
    });
  });
  if (weightsAboveUnit) messages.value.push("Some weights are >=1.");
  if (zeroOrNegativeValues) messages.value.push("Some weights are <=0.")
}

function proceedFourthStep() {
  messages.value = [];
  if (!isLikertScale()) {
    const allMargins = form.kpi.kpi_margins_attributes
    for(let m in allMargins) {
      const margin = allMargins[m]
      const isMonotonic = Utils.methods.isMonotonic([margin.m0, margin.m1, margin.m2, margin.m3])
      if (margin.hasOwnProperty('level') && ['error', 'null'].includes(isMonotonic))
        messages.value.push(`Please provide correct margins (${margin.level}).`)
    }
  } else {
    form.kpi.kpi_margins_attributes = []
  }
}

function selectedAggregationLevelsText() {
  return form.aggregationLevels.map((l) => l.text).join(", ");
}

function onNameChange($event) {
  const name = $event;
  const kpi = form.kpi;
  if (props.defaultKpiValues && props.defaultKpiValues[name] !== undefined) {
    //clean up
    form.aggregationLevels = [];
    const defaultValues = props.defaultKpiValues[name];
    form.aggregationLevels = defaultValues["aggregationLevels"];
    kpi.dimension_attributes = defaultValues["kpi"]["dimension_attributes"];
    kpi.sectors_attributes = defaultValues["kpi"]["sectors_attributes"];

    // fix aggregation levels
    form.aggregationLevels.forEach(level => {
      if (level.text === 'Peb')
        level.text = 'PEB';
      else if (level.text === 'Ped')
        level.text = 'PED';
    })

    //margins
    const defaultMargins = defaultValues["kpi"]["margins_attributes"];
    const inputMargins = form.kpi.kpi_margins_attributes;
    inputMargins.splice(0);
    if (defaultMargins instanceof Array) {
      inputMargins.push({
        level: "common",
        m0: defaultMargins[0],
        m1: defaultMargins[1],
        m2: defaultMargins[2],
        m3: defaultMargins[3],
      });
    } else if (defaultMargins instanceof Object) {
      const keys = Object.keys(defaultMargins);
      for (let i = 0; i < keys.length; i++) {
        const m = defaultMargins[keys[i]];
        inputMargins.push({
          level: keys[i],
          m0: m[0],
          m1: m[1],
          m2: m[2],
          m3: m[3],
        });
      }
    }
  } else {
    kpi.dimension_attributes = { name: null };
    kpi.sectors_attributes = [];
    kpi.kpi_margins_attributes = [{ level: 'common', m0: null, m1: null, m2: null, m3: null }];
    form.aggregationLevels = [];
  }
}

function marginsValidationMethods() {
  const self = this;
  return {
    validateSetOfMargins: function (margins) {
      const marginsLevel = margins.level;
      for (let j = 0; j < 4; j++) {
        const currentMargin = margins[`m${j}`];
        if (self.isEmptyString(currentMargin)) {
          self.messages.value.push(
            `Please provide all margins for ${self.clearAndCapitalize(
              marginsLevel
            )}.`
          );
          break;
        }
      }

      if (
        self.isMonotonic([margins.m0, margins.m1, margins.m2, margins.m3]) ===
        "error"
      )
        self.messages.value.push(
          `Please provide ascending or descending margins for ${self.clearAndCapitalize(
            marginsLevel
          )}.`
        );
    },
  };
}
function isLikertScale() {
  const likert = form.kpi.likert;
  const likertDataType = typeof likert;

  if (likertDataType === "boolean") return likert;
  else if (likertDataType === "string")
    return form.kpi.likert !== "false" && form.kpi.likert !== null;
  else return false;
}
function kpiMarginsList() {
  return form.kpi.kpi_margins_attributes;
}
</script>

<style lang="css" scoped>
.agg-level {
  background: #fff;
  border-radius: 5px;
  color: #000;
  margin-bottom: 10px;
}

.bg-header-gradient {
  background: linear-gradient(to right, #cdeffa, #b2d0fa);
}

.v-stepper-header .v-stepper-item {
  font-size: 21px;
}
</style>
